import { ArrowUpRight } from "lucide-react";
import { Seo, Eyebrow, CTAButton } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { MemoryGraphDiagram } from "@/components/site/Diagrams";

const CAPS = [
  { t: "Agent-driven research", d: "Autonomous agents research accounts and contacts, assembling context a rep would spend hours gathering." },
  { t: "Memory-native prospect context", d: "Every prospect and account has persistent, owned memory on the same substrate as Smritium — history that compounds." },
  { t: "Pipeline intelligence", d: "Buyer-intent modelling and relationship context surface the next best action, ranked by evidence." },
];

export default function ASOC() {
  return (
    <div data-testid="asoc-page">
      <Seo title="ASOC — AI revenue operating system" description="ASOC is an AI revenue operating system for outbound sales — memory-native pipeline intelligence." />
      <PageHero
        eyebrow="Product · ASOC"
        title="An AI revenue operating system for outbound sales."
        lead="Memory-native pipeline intelligence, built on the same substrate as Smritium. Prospect memory, relationship context and buyer-intent that compound instead of resetting between reps, tools and quarters."
      >
        <CTAButton to="/company/contact" variant="solid">
          Talk to us <ArrowUpRight className="h-4 w-4" />
        </CTAButton>
      </PageHero>

      <Section>
        <Stagger className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {CAPS.map((c) => (
            <Item key={c.t} className="bg-ink p-8 md:p-10">
              <h3 className="font-serif text-xl text-bone">{c.t}</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-mist">{c.d}</p>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>The idea</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
              Your revenue memory is your moat.
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-mist">
              Outbound teams lose context constantly — between reps, between tools, between quarters. ASOC keeps the
              account graph — people, organisations, and every touch — as owned, attributed memory. Handoffs carry the
              full history; nothing starts from zero.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="border border-white/10 bg-white/[0.015] p-8">
              <MemoryGraphDiagram className="text-bone/70" />
              <p className="mt-4 font-mono text-xs text-mist">Fig. 1 — The account graph as owned memory.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal className="mx-auto max-w-3xl">
          <Eyebrow>In practice</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
            From cold list to warm pipeline, with memory that stays.
          </h2>
          <p className="mt-6 text-[17px] leading-relaxed text-mist">
            A rep inherits an account and, instead of a blank CRM record, sees the full history: prior conversations,
            the champion who moved teams, the objection raised last quarter, the research an agent gathered this
            morning. Every insight cites where it came from. When the rep hands the account on, none of that context is
            lost — because it lives in the substrate, not in someone's head or a vendor's model.
          </p>
        </Reveal>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <h2 className="max-w-xl text-balance font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
            See ASOC on your pipeline.
          </h2>
          <CTAButton to="/company/contact" variant="outline">
            Talk to us <ArrowUpRight className="h-4 w-4" />
          </CTAButton>
        </Reveal>
      </Section>
    </div>
  );
}
